// NFe Types and Interfaces

export enum NFeTipoOperacao {
  Venda = 0,
  Compra = 1,
  DevolucaoFornecedor = 2,
  DevolucaoCliente = 3,
}

export enum NFeStatus {
  Rascunho = 'D',
  Completa = 'C',
  Processando = 'P',
  Autorizada = 'A',
  Rejeitada = 'R',
  Cancelada = 'X',
}

export enum NFeTipoDestinatario {
  Fisica = 'F',
  Juridica = 'J',
}

export enum NFeModalidadeFrete {
  ContaRemetente = 0,
  ContaDestinatario = 1,
  ContaTerceiros = 2,
  ProprioRemetente = 3,
  ProprioDestinatario = 4,
  SemFrete = 9,
}

export interface EmitenteNFe {
  cnpj?: string;
  nomeRazaoSocial: string;
  nomeFantasia?: string;
  uf: string;
  inscricaoEstadual?: string;
  regimeTributario: string; // CRT: "1"=Simples, "2"=Presumido, "3"=Real
}

// Main NFe interfaces
export interface EmissaoNFe {
  emissaoId: string;
  nFeId: string;
  empresaId: string;
  empresaNome?: string;
  chave?: string;
  numero: number;
  serie: number;
  modelo: string; // "55" or "65"
  naturezaOperacao: string;
  finalidadeNFe: string; // "1"-Normal, "2"-Complementar, "3"-Ajuste, "4"-Devolução
  tipoNota: string; // "0"-Entrada, "1"-Saída
  tipoAmbiente: string; // "1"-Produção, "2"-Homologação
  indicadorDestinatario?: string;
  indicadorFinal?: string;
  indicadorPresenca?: string;
  dataEmissao: string; // ISO 8601
  dataSaidaEntrada?: string; // ISO 8601
  tipoOperacao: NFeTipoOperacao;
  status: NFeStatus;
  valorTotal: number;
  emitente: EmitenteNFe;
  destinatario?: DestinatarioNFe;
  itens: ItemNFe[];
  duplicatas: DuplicataNFe[];
  transporte?: TransporteNFe;
  informacoesFisco?: string;
  informacoesComplementares?: string;
  totais: TotaisNFe;
  protocolo?: string;
  dhRecbto?: string;
  referencias?: ReferenciaNFe[];
  ultimaRejeicaoCstat?: number;
  ultimaRejeicaoXMotivo?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface DestinatarioNFe {
  tipo: NFeTipoDestinatario;
  cnpj?: string;
  cpf?: string;
  nomeRazaoSocial: string;
  indicadorIE?: string;
  inscricaoEstadual?: string;
  logradouro: string;
  numero: string;
  complemento?: string;
  bairro: string;
  municipio: string;
  uf: string;
  cep: string;
  email?: string;
  telefone?: string;
  municipioId?: number;
}

export interface MunicipioItem {
  id: number;
  nome: string;
  uf: string;
  ibge: string;
}

export interface TributacaoItemNFe {
  origem?: string;
  cst?: string;
  csosn?: string;
  reducaoBcIcms?: number;
  baseCalculoIcms?: number;
  aliquotaIcms?: number;
  valorIcms?: number;
  aliquotaCreditoSN?: number;
  aliquotaFcp?: number;
  aliquotaFcpSt?: number;
  mva?: number;
  reducaoBcSt?: number;
  baseCalculoSt?: number;
  aliquotaSt?: number;
  valorSt?: number;
  baseStRetido?: number;
  valorStRetido?: number;
  cstIpi?: string;
  baseCalculoIpi?: number;
  aliquotaIpi?: number;
  valorIpi?: number;
  cstPis?: string;
  baseCalculoPis?: number;
  aliquotaPis?: number;
  valorPis?: number;
  cstCofins?: string;
  baseCalculoCofins?: number;
  aliquotaCofins?: number;
  valorCofins?: number;
}

export interface ItemNFe {
  id: string;
  numeroItem: number;
  produtoId?: string;
  regraFiscalId?: string;
  regraFiscalCodigo?: number;
  regraFiscalDescricao?: string;
  codigoProduto: string;
  codigoEAN?: string;
  nomeProduto: string;
  ncm?: string;
  cest?: string;
  cfop?: string;
  unidadeComercial: string;
  quantidadeComercial: number;
  valorUnitario: number;
  valorTotal: number;
  valorDesconto?: number;
  valorFrete?: number;
  valorSeguro?: number;
  valorOutros?: number;
  infAdProd?: string;
  tributacao: TributacaoItemNFe;
}

export interface DuplicataNFe {
  id: string; // UUID
  numero: string;
  dataVencimento: string; // ISO 8601
  valor: number;
}

export interface TransporteNFe {
  modalidadeFrete: string;
  nomeTransportadora?: string;
  cnpjTransportadora?: string;
  cpfTransportadora?: string;
  inscricaoEstadual?: string;
  endereco?: string;
  municipio?: string;
  uf?: string;
  placa?: string;
  ufVeiculo?: string;
}

export interface InformacoesAdicionaisNFe {
  informacoesComplementares?: string;
  informacoesFisco?: string;
}

export interface TotaisNFe {
  baseCalculoICMS: number;
  valorICMS: number;
  baseCalculoICMSST: number;
  valorICMSST: number;
  valorProdutos: number;
  valorFrete: number;
  valorSeguro: number;
  valorDesconto: number;
  valorII: number;
  valorIPI: number;
  valorPIS: number;
  valorCOFINS: number;
  valorOutros: number;
  valorNota: number;
  valorTotalTributos: number;
}

// List response
export interface ListarNFesResponse {
  emissoes: NFeSummary[];
  totalRegistros: number;
  paginaAtual: number;
  tamanhoPagina: number;
  totalPaginas: number;
}

export interface NFeSummary {
  id: string;
  nFeId: string;
  empresaId: string;
  empresaNome: string;
  chave?: string;
  numero: number;
  serie: number;
  dataEmissao: string;
  tipoNota: string; // "0"=Entrada, "1"=Saída
  status: NFeStatus;
  destinatarioNome?: string;
  destinatarioCnpjCpf?: string;
  valorTotal: number;
  quantidadeItens: number;
  ultimaRejeicaoCstat?: number;
  ultimaRejeicaoXMotivo?: string;
  createdAt: string;
  updatedAt?: string;
}

// Request DTOs
export interface CriarRascunhoNFeRequest {
  empresaId: string; // UUID
  serie: number;
  modelo: string; // "55" or "65"
  naturezaOperacao: string;
  finalidadeNFe: string; // "1"-Normal, "2"-Complementar, "3"-Ajuste, "4"-Devolução
  indicadorDestinatario?: string; // "1"-Interna, "2"-Interestadual, "3"-Exterior
  tipoNota: string; // "0"-Entrada, "1"-Saída
  indicadorFinal?: string; // "0"-Normal, "1"-Consumidor final
  indicadorPresenca?: string; // "0"-Não se aplica, "1"-Presencial, "2"-Internet, etc.
  tipoEmissao: string; // "1"-Normal
  fornecedorId?: string; // UUID
}

export interface CriarRascunhoNFeResponse {
  emissaoId: string; // UUID
  nfeId: string; // UUID
  serie: number;
  numero: number;
  naturezaOperacao: string;
  mensagem: string;
}

export interface AtualizarCabecalhoRequest {
  serie: number;
  numero: number;
  naturezaOperacao: string;
  finalidadeNFe: string;
  tipoNota: string;
  indicadorDestinatario?: string;
  indicadorFinal?: string;
  indicadorPresenca?: string;
  empresaId?: string;
  dhSaiEnt?: string; // ISO 8601
}

export interface AtualizarDestinatarioRequest {
  tipo: NFeTipoDestinatario;
  cnpj?: string;
  cpf?: string;
  nomeRazaoSocial: string;
  indicadorIE?: string;
  inscricaoEstadual?: string;
  logradouro: string;
  numero: string;
  complemento?: string;
  bairro: string;
  municipioId: number;
  cep: string;
  email?: string;
  telefone?: string;
}

export interface RegraFiscalNFeItem {
  id: string;
  codigo: number;
  descricao: string;
  cst?: string;
  csosn?: string;
  cfop?: string;
  ncm?: string;
  cest?: string;
  origemMercadoria?: string;
  aliquota?: number;
  aliquotaSt?: number;
  mva?: number;
  redBc?: number;
  redBcSt?: number;
  cstPis?: string;
  aliquotaPis?: number;
  cstCofins?: string;
  aliquotaCofins?: number;
}

export interface BuscarProdutosNFeItem {
  id: string;
  codigo: number;
  descricao: string;
  valor: number;
  pmc?: number;
  unidade?: string;
}

export interface ProdutoFiscalNFeResponse {
  id: string;
  codigo: number;
  descricao: string;
  valor: number;
  pmc?: number;
  unidade?: string;
  codigoEan?: string;
  regraFiscalId?: string;
  regraFiscalCodigo?: number;
  regraFiscalDescricao?: string;
  cst?: string;
  csosn?: string;
  cfop?: string;
  ncm?: string;
  cest?: string;
  origemMercadoria?: string;
  aliquota?: number;
  aliquotaSt?: number;
  mva?: number;
  redBc?: number;
  redBcSt?: number;
  cstPis?: string;
  aliquotaPis?: number;
  cstCofins?: string;
  aliquotaCofins?: number;
}

export interface AdicionarItemRequest {
  produtoId?: string;
  regraFiscalId?: string;
  codigoProduto: string;
  nomeProduto: string;
  ncm: string;
  cest?: string;
  cfop: string;
  unidadeComercial: string;
  quantidadeComercial: number;
  valorUnitario: number;
  valorDesconto?: number;
  valorFrete?: number;
  valorSeguro?: number;
  valorOutros?: number;
}

export interface AdicionarItemComProdutoRequest {
  produtoId: string;
  quantidade: number;
  valorUnitario: number;
}

export interface AtualizarItemRequest extends Partial<AdicionarItemRequest> {
  infAdProd?: string;
}

export interface AtualizarTributacaoItemRequest {
  origem?: string;
  cst?: string;
  csosn?: string;
  // ICMS
  reducaoBC?: number;
  baseCalculoICMS?: number;
  aliquotaICMS?: number;
  valorICMS?: number;
  aliquotaCreditoSN?: number;
  aliquotaFCP?: number;
  aliquotaFCPST?: number;
  // ICMS-ST
  mva?: number;
  reducaoBCST?: number;
  baseCalculoST?: number;
  aliquotaST?: number;
  valorST?: number;
  // ICMS retido anteriormente
  baseSTRetido?: number;
  valorSTRetido?: number;
  // IPI
  cstipi?: string;
  baseCalculoIPI?: number;
  aliquotaIPI?: number;
  valorIPI?: number;
  // PIS
  cstpis?: string;
  baseCalculoPIS?: number;
  aliquotaPIS?: number;
  valorPIS?: number;
  // COFINS
  cstcofins?: string;
  baseCalculoCOFINS?: number;
  aliquotaCOFINS?: number;
  valorCOFINS?: number;
}

export interface AdicionarDuplicataRequest {
  numero: string;
  dataVencimento: string;
  valor: number;
}

export interface AtualizarDuplicataRequest extends Partial<AdicionarDuplicataRequest> {
  // All fields optional for update
}

export interface AtualizarTransporteRequest {
  modalidadeFrete: string;
  nomeTransportadora?: string;
  cnpjTransportadora?: string;
  cpfTransportadora?: string;
  inscricaoEstadual?: string;
  enderecoCompleto?: string;
  nomeMunicipio?: string;
  uf?: string;
  placaVeiculo?: string;
  ufVeiculo?: string;
}

export interface AtualizarInformacoesAdicionaisRequest {
  informacoesComplementares?: string;
  informacoesFisco?: string;
}

export interface ReferenciaNFe {
  id: string;
  chaveNFeReferenciada: string;
}

export interface AdicionarReferenciaRequest {
  chaveNFeReferenciada: string;
}

export interface TransmitirNFeResponse {
  chaveAcesso: string;
  protocolo: string;
  status: string;
  mensagem: string;
}

export interface CancelarNFeRequest {
  justificativa: string;
}

// Filters for list
export interface NFeFiltros {
  empresaIds?: string[];
  status?: NFeStatus;
  dataInicio?: string; // ISO 8601
  dataFim?: string;
  pageNumber: number;
  pageSize: number;
}

// Helper functions
export const getTipoNotaLabel = (tipoNota: string): string =>
  tipoNota === '0' ? 'Entrada' : tipoNota === '1' ? 'Saída' : '—';

export const getNFeTipoOperacaoLabel = (tipo: NFeTipoOperacao): string => {
  switch (tipo) {
    case NFeTipoOperacao.Venda:
      return 'Venda';
    case NFeTipoOperacao.Compra:
      return 'Compra';
    case NFeTipoOperacao.DevolucaoFornecedor:
      return 'Devolução p/ Fornecedor';
    case NFeTipoOperacao.DevolucaoCliente:
      return 'Devolução de Cliente';
    default:
      return 'Desconhecido';
  }
};

export const getNFeStatusLabel = (status: NFeStatus): string => {
  switch (status) {
    case NFeStatus.Rascunho:
      return 'Rascunho';
    case NFeStatus.Completa:
      return 'Completa';
    case NFeStatus.Processando:
      return 'Processando';
    case NFeStatus.Autorizada:
      return 'Autorizada';
    case NFeStatus.Rejeitada:
      return 'Rejeitada';
    case NFeStatus.Cancelada:
      return 'Cancelada';
    default:
      return 'Desconhecido';
  }
};

export const getNFeStatusBadgeVariant = (status: NFeStatus): string => {
  switch (status) {
    case NFeStatus.Rascunho:
      return 'secondary';
    case NFeStatus.Completa:
      return 'info';
    case NFeStatus.Processando:
      return 'warning';
    case NFeStatus.Autorizada:
      return 'success';
    case NFeStatus.Rejeitada:
      return 'danger';
    case NFeStatus.Cancelada:
      return 'dark';
    default:
      return 'light';
  }
};

export const getModalidadeFreteLabel = (modalidade: NFeModalidadeFrete): string => {
  switch (modalidade) {
    case NFeModalidadeFrete.ContaRemetente:
      return '0 - Conta do Remetente';
    case NFeModalidadeFrete.ContaDestinatario:
      return '1 - Conta do Destinatário';
    case NFeModalidadeFrete.ContaTerceiros:
      return '2 - Conta de Terceiros';
    case NFeModalidadeFrete.ProprioRemetente:
      return '3 - Próprio Remetente';
    case NFeModalidadeFrete.ProprioDestinatario:
      return '4 - Próprio Destinatário';
    case NFeModalidadeFrete.SemFrete:
      return '9 - Sem Frete';
    default:
      return 'Desconhecido';
  }
};
